import { useQuery } from '@tanstack/vue-query';
import { computed } from 'vue';
import http from '@/services/http';

export interface RawDdexPartyOption {
  id: number;
  name?: string;
  party_id: string;
}

export interface PartyDisplayItem {
  id: string;
  name: string;
}

/** Shared fetch for raw DDEX parties. Used by useRawDdexParties and by useFilterOptions (single-query path). */
export async function fetchRawDdexParties(): Promise<RawDdexPartyOption[]> {
  const response = await http.get('/api/raw_ddex_party');
  const responseData = response.data as { data?: RawDdexPartyOption[] } | RawDdexPartyOption[];
  const partyItems = Array.isArray(responseData) ? responseData : (responseData.data ?? []);
  return partyItems.map((partyItem: RawDdexPartyOption) => ({
    id: partyItem.id,
    name: partyItem.name,
    party_id: partyItem.party_id ?? String(partyItem.id),
  }));
}

/**
 * Fetches raw DDEX parties from GET /api/raw_ddex_party.
 * Used for Match Sender Party Ids and Match Sent On Behalf Of Party Ids filters,
 * and for resolving party_id to display name in the track matching table.
 */
export function useRawDdexParties(enabled = true) {
  const query = useQuery({
    queryKey: ['rawDdexParties'],
    queryFn: fetchRawDdexParties,
    retry: 0,
    enabled: computed(() => Boolean(enabled)),
  });

  const idToName = computed(() => {
    const partiesList = query.data.value ?? [];
    const idToDisplayName: Record<number, string> = {};
    partiesList.forEach((party) => {
      idToDisplayName[party.id] = party.name ?? party.party_id;
    });
    return idToDisplayName;
  });

  /** Selector: resolve numeric party ids to { id, name } for display (e.g. in table cells). */
  function selectPartyDisplayNamesById(ids: number[] | unknown): PartyDisplayItem[] {
    if (!Array.isArray(ids)) return [];
    return ids.map((id) => {
      const numericId = typeof id === 'number' ? id : Number(id);
      const idString = String(numericId);
      const name = !isNaN(numericId) ? idToName.value[numericId] ?? idString : idString;
      return { id: idString, name };
    });
  }

  return {
    ...query,
    selectPartyDisplayNamesById,
  };
}
